import { useMemo, type ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMobileLayout } from '@/utils/useMobileLayout';
import { cn } from '@/lib/utils';

export interface DataListColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  sortable?: boolean;
  /** For TanStack sort accessor; if omitted, no automatic sort. */
  accessorFn?: (row: T) => unknown;
  className?: string;
  /** Hide this column when below the given breakpoint. */
  hideBelow?: 'sm' | 'md' | 'lg';
}

export interface DataListPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

interface DataListProps<T> {
  data: T[];
  columns: DataListColumn<T>[];
  mobileCard?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyState?: ReactNode;
  pagination?: DataListPagination;
  getRowId?: (row: T) => string | number;
  className?: string;
  /** Sticky table header. */
  stickyHeader?: boolean;
  /** Custom initial sort. */
  initialSorting?: SortingState;
  /**
   * Enable a leading checkbox column. When true, `getRowId` must be set so
   * selection identity is stable across pagination + sorting. The parent
   * owns the selected set so it can render bulk-action UI.
   */
  selectable?: boolean;
  selectedIds?: Set<string | number>;
  onSelectionChange?: (next: Set<string | number>) => void;
}

const hideBelowClass = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
} as const;

export function DataList<T>({
  data,
  columns,
  mobileCard,
  onRowClick,
  loading,
  emptyState,
  pagination,
  getRowId,
  className,
  stickyHeader,
  initialSorting,
  selectable,
  selectedIds,
  onSelectionChange,
}: DataListProps<T>) {
  const { isMobile } = useMobileLayout();

  // Selection identity. The parent owns the set; we offer helpers tied to the
  // current data slice (header all/none toggle, per-row toggle).
  const selection = selectedIds ?? new Set<string | number>();

  const rowIds = useMemo<(string | number)[]>(() => {
    if (!selectable || !getRowId) return [];
    return data.map((row) => getRowId(row));
  }, [selectable, getRowId, data]);

  const visibleSelectedCount = useMemo(() => {
    if (!selectable) return 0;
    let n = 0;
    for (const id of rowIds) if (selection.has(id)) n += 1;
    return n;
  }, [selectable, rowIds, selection]);

  const allVisibleSelected =
    selectable && rowIds.length > 0 && visibleSelectedCount === rowIds.length;
  const someVisibleSelected =
    selectable && visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleAllVisible = () => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (allVisibleSelected) {
      for (const id of rowIds) next.delete(id);
    } else {
      for (const id of rowIds) next.add(id);
    }
    onSelectionChange(next);
  };

  const toggleRow = (id: string | number) => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const tanstackColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((col) => ({
        id: col.id,
        header: col.header as string,
        accessorFn: col.accessorFn,
        cell: (info) => col.cell(info.row.original),
        enableSorting: Boolean(col.sortable && col.accessorFn),
      })),
    [columns],
  );

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: initialSorting ? { sorting: initialSorting } : undefined,
    getRowId: getRowId
      ? (row, index) => String(getRowId(row) ?? index)
      : undefined,
  });

  // Mobile card list
  if (isMobile && mobileCard) {
    return (
      <div className={cn('space-y-3', className)}>
        {selectable && data.length > 0 && (
          <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
            <Checkbox
              checked={
                allVisibleSelected
                  ? true
                  : someVisibleSelected
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={toggleAllVisible}
              aria-label="Select all on this page"
            />
            <span className="text-xs text-muted-foreground">
              {visibleSelectedCount > 0
                ? `${visibleSelectedCount} selected`
                : 'Select all on this page'}
            </span>
          </div>
        )}
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))
        ) : data.length === 0 ? (
          emptyState ?? <DefaultEmpty />
        ) : (
          data.map((row, i) => {
            const key = getRowId ? getRowId(row) : i;
            const checked = selectable && selection.has(key as string | number);
            return (
              <div key={String(key)} className="relative">
                {selectable && (
                  <div
                    className="absolute left-2 top-2 z-10 rounded bg-background/90 p-1 shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={Boolean(checked)}
                      onCheckedChange={() =>
                        toggleRow(key as string | number)
                      }
                      aria-label="Select row"
                    />
                  </div>
                )}
                <div
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(onRowClick && 'cursor-pointer')}
                >
                  {mobileCard(row)}
                </div>
              </div>
            );
          })
        )}
        {pagination && <PaginationBar pagination={pagination} />}
      </div>
    );
  }

  // Desktop table
  return (
    <div className={cn('space-y-3', className)}>
      <div className="rounded-md border border-border bg-card">
        <Table>
          <TableHeader className={cn(stickyHeader && 'sticky top-0 bg-card z-10')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {selectable && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        allVisibleSelected
                          ? true
                          : someVisibleSelected
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={toggleAllVisible}
                      aria-label="Select all on this page"
                    />
                  </TableHead>
                )}
                {headerGroup.headers.map((header) => {
                  const col = columns.find((c) => c.id === header.id);
                  const sortable = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(col?.className, col?.hideBelow && hideBelowClass[col.hideBelow])}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDir === 'asc' ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : sortDir === 'desc' ? (
                            <ArrowDown className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, rowIdx) => (
                <TableRow key={rowIdx}>
                  {selectable && (
                    <TableCell className="w-10">
                      <Skeleton className="h-4 w-4" />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      className={cn(col.hideBelow && hideBelowClass[col.hideBelow])}
                    >
                      <Skeleton className="h-4 w-full max-w-[160px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0">
                  {emptyState ?? <DefaultEmpty />}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const rowKey =
                  getRowId
                    ? (getRowId(row.original) as string | number)
                    : row.id;
                const checked = selectable && selection.has(rowKey);
                return (
                  <TableRow
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      checked && 'bg-muted/40',
                    )}
                  >
                    {selectable && (
                      <TableCell
                        className="w-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={Boolean(checked)}
                          onCheckedChange={() => toggleRow(rowKey)}
                          aria-label="Select row"
                        />
                      </TableCell>
                    )}
                    {row.getVisibleCells().map((cell) => {
                      const col = columns.find((c) => c.id === cell.column.id);
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(col?.className, col?.hideBelow && hideBelowClass[col.hideBelow])}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {pagination && <PaginationBar pagination={pagination} />}
    </div>
  );
}

function DefaultEmpty() {
  return (
    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
      No results.
    </div>
  );
}

function PaginationBar({ pagination }: { pagination: DataListPagination }) {
  const {
    page,
    pageSize,
    total,
    totalPages: totalPagesProp,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50, 100],
  } = pagination;
  const totalPages = totalPagesProp ?? Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-muted-foreground">
        {total === 0 ? 'No items' : `Showing ${start}–${end} of ${total}`}
      </div>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-9 w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                aria-disabled={!canPrev}
                tabIndex={canPrev ? 0 : -1}
                className={cn(!canPrev && 'pointer-events-none opacity-50')}
                onClick={(e) => {
                  e.preventDefault();
                  if (canPrev) onPageChange(page - 1);
                }}
                href="#"
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink isActive href="#" onClick={(e) => e.preventDefault()}>
                {page}
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                aria-disabled={!canNext}
                tabIndex={canNext ? 0 : -1}
                className={cn(!canNext && 'pointer-events-none opacity-50')}
                onClick={(e) => {
                  e.preventDefault();
                  if (canNext) onPageChange(page + 1);
                }}
                href="#"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        {/* Use total as a visual hint */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden sm:inline-flex"
          disabled
        >
          of {totalPages}
        </Button>
      </div>
    </div>
  );
}

export default DataList;
